import BottomSheet from '@components/BottomSheet'
import EmptyList from '@components/EmptyList'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { useInfiniteScroll } from '@hooks/useInfiniteScroll'
import { useEarnVaults } from '@plug-core/hooks/earn/useEarnVaults'
import useConfig from '@plug-core/hooks/useConfig'
import { EarnVault, EarnVaultSortBy } from '@plug-core/interfaces/earn'
import { ListGroup, Spinner, cn } from '@ui'
import { getWindowDimensions } from '@utils/windows'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { VaultDetails } from './VaultDetails'
import { VaultFilters } from './VaultFilters'
import { VaultListItem } from './VaultListItem'

export const VaultsList = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)

  const [chainId, setChainId] = useState<number | undefined>()
  const [protocol, setProtocol] = useState<string | undefined>()
  const [asset, setAsset] = useState<string | undefined>()
  const [sortBy, setSortBy] = useState<EarnVaultSortBy>('tvl')
  const [displayedVault, setDisplayedVault] = useState<EarnVault>()

  const { tokens } = useConfig()
  const { chains, protocols, vaults, vaultsLoading, fetchNextPage } = useEarnVaults({
    chainId,
    protocol,
    sortBy,
    asset,
  })

  const supportedTokens = useMemo(() => {
    const supportedChainIds = new Set(chains.map((c) => c.chainId))

    return tokens.filter((tk) => tk.evmChain && supportedChainIds.has(tk.evmChain.chainId))
  }, [tokens, chains])

  useInfiniteScroll({
    containerRef: scrollRef,
    onNearBottom: fetchNextPage,
  })

  return (
    <div
      className="flex flex-col gap-3 overflow-hidden"
      style={{ height: getWindowDimensions().height - 48 }}
    >
      <Titlebar title={t('earn.openNew')} onActionClick={() => navigate(-1)} />

      <VaultFilters
        chains={chains}
        protocols={protocols}
        tokens={supportedTokens}
        chainId={chainId}
        protocol={protocol}
        asset={asset}
        sortBy={sortBy}
        onChainChange={setChainId}
        onProtocolChange={setProtocol}
        onAssetChange={setAsset}
        onSortChange={setSortBy}
      />

      <div
        ref={scrollRef}
        className={cn(
          'flex min-h-0 flex-1 flex-col',
          displayedVault ? 'overflow-y-hidden' : 'overflow-y-auto'
        )}
      >
        {!vaultsLoading && vaults.length === 0 && (
          <div className="mt-8 flex flex-col">
            <EmptyList
              emoji="🫥"
              title={t('earn.noVaults')}
              description={t('earn.noVaultsDescription')}
            />
          </div>
        )}

        {vaults.length > 0 && (
          <ListGroup>
            {vaults.map((vault) => (
              <VaultListItem
                key={`${vault.chainId}-${vault.address}`}
                vault={vault}
                onClick={setDisplayedVault}
              />
            ))}
          </ListGroup>
        )}

        {vaultsLoading && (
          <div className="flex flex-col items-center py-4 text-fg-2">
            <Spinner size={20} />
          </div>
        )}
      </div>

      {displayedVault && (
        <BottomSheet visible onBackdropClick={() => setDisplayedVault(undefined)}>
          <VaultDetails vault={displayedVault} onClose={() => setDisplayedVault(undefined)} />
        </BottomSheet>
      )}
    </div>
  )
}

export default VaultsList
